import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { apiErrorMessage } from '@/lib/api-errors';
import { useConnectStripe, useDisconnectStripe, useStripeStatus } from './api';
import { ProviderLogo } from './ProviderLogo';

export function StripeIntegration({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('integrations');
  const { data: status, isLoading } = useStripeStatus(workspaceId);
  const connect = useConnectStripe(workspaceId);
  const disconnect = useDisconnectStripe(workspaceId);
  const { confirm, ConfirmDialog } = useConfirm();

  async function handleConnect() {
    try {
      const result = await connect.mutateAsync();
      window.location.assign(result.url);
    } catch (err) {
      toast.error(t('stripe.connectFailed'), { description: apiErrorMessage(err) });
    }
  }

  async function handleDisconnect() {
    const confirmed = await confirm({
      title: t('stripe.disconnectConfirmTitle'),
      description: t('stripe.disconnectConfirmDescription'),
      confirmLabel: t('stripe.disconnect'),
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await disconnect.mutateAsync();
      toast.success(t('stripe.disconnected'));
    } catch (err) {
      toast.error(t('stripe.disconnectFailed'), { description: apiErrorMessage(err) });
    }
  }

  return (
    <Card>
      <ConfirmDialog />
      <CardHeader>
        <ProviderLogo provider="stripe" />
        <CardTitle>{t('stripe.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading ? (
          <p className="text-sm text-muted">{t('common:actions.loading')}</p>
        ) : !status?.connected ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-ink">{t('stripe.notConnected')}</div>
              <p className="text-xs text-muted">{t('stripe.notConnectedDescription')}</p>
            </div>
            <Button loading={connect.isPending} onClick={handleConnect}>
              {t('stripe.connect')}
            </Button>
          </div>
        ) : !status.chargesEnabled ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-warning">{t('stripe.onboardingIncomplete')}</div>
              <p className="text-xs text-muted">{t('stripe.onboardingDescription', { account: status.accountIdMasked })}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" loading={connect.isPending} onClick={handleConnect}>
                {t('stripe.resumeOnboarding')}
              </Button>
              <Button variant="outline" loading={disconnect.isPending} onClick={handleDisconnect}>
                {t('stripe.disconnect')}
              </Button>
            </div>
          </div>
        ) : status.actionRequired ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-warning">{t('stripe.payoutsHeld')}</div>
              <p className="text-xs text-muted">{t('stripe.payoutsHeldDescription')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" loading={connect.isPending} onClick={handleConnect}>
                {t('stripe.completeSetup')}
              </Button>
              <Button variant="outline" loading={disconnect.isPending} onClick={handleDisconnect}>
                {t('stripe.disconnect')}
              </Button>
            </div>
          </div>
        ) : !status.payoutsEnabled ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-ink">{t('stripe.verifying')}</div>
              <p className="text-xs text-muted">{t('stripe.verifyingDescription')}</p>
            </div>
            <Button variant="outline" loading={disconnect.isPending} onClick={handleDisconnect}>
              {t('stripe.disconnect')}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="bg-success/15 px-2.5 py-1 text-xs font-bold text-success">{t('stripe.connectedCheck')}</span>
            <span className="font-mono text-sm text-muted">{status.accountIdMasked}</span>
            <span className="ml-auto bg-success/15 px-2.5 py-1 text-[11px] font-bold uppercase text-success">{t('stripe.chargesEnabled')}</span>
            <Button variant="outline" loading={disconnect.isPending} onClick={handleDisconnect}>
              {t('stripe.disconnect')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
